import { Component } from '@angular/core';
import { AccountFinderDialogComponent } from 'app/admin/account-finder-dialog.component';
import { ProductFinderDialogComponent } from 'app/inventory/product-finder-dialog.component';
import { Account, PoDetail, PoDetailType, PoHeader, Product } from 'app/model/entities/entity-model';
import { PoDetailTypeCode } from 'app/model/enums/po-detail-type-code';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { EntityFinderDialog } from 'app/shared/entity-finder.dialog';
import _ from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface EditPodDialogParams extends DialogParams {
  poHeader: PoHeader;
  poDetail?: PoDetail; // if null - then dialog will create a poDetail else edit this one.
}

@Component({
    templateUrl: './edit-pod-dialog.component.html',
    standalone: false
})
export class EditPodDialogComponent extends DialogBaseComponent<EditPodDialogParams> {

  poHeader: PoHeader;
  poDetail: PoDetail;
  poDetailTypes: PoDetailType[];
  allAccounts: Account[];
  matchingAccounts: Account[];


  constructor( public config: DynamicDialogConfig<EditPodDialogParams>, public dialogRef: DynamicDialogRef, public domainService: DomainService) {
    super(config, dialogRef, domainService);

    Object.assign(this.config, {
      width: '800px',
      height: '550px',
    });
    if (this.config.header == null) {
      this.config.header = "Purchase Order Detail";
    }
    this.prepare();
  }

  async prepare() {

    this.poHeader = this.params.poHeader;
    this.poDetail = this.params.poDetail;

    this.poDetailTypes = await this.dbQueryService.cacheAndGetAll(PoDetailType);
    this.allAccounts = await this.dbQueryService.getReissuanceAccounts();

    const nullAccount = <Account> { accountName: '[None]' };
    this.allAccounts.unshift(nullAccount);

    if (this.poDetail == null) {
      const podStruct = <PoDetail>{};
      const now = new Date();
      podStruct.poHeaderId = this.poHeader.id;
      podStruct.poDetailTypeId = PoDetailTypeCode.Stock;
      podStruct.orderQty = 1;

      this.poDetail = this.uow.createEntity(PoDetail, podStruct);
    }

    // remove 'Order' if this is a new poDetail - we should not be able to create an PO based on a Job Order
    if (this.poDetail.joDetailId == null) {
      this.poDetailTypes = this.poDetailTypes.filter(x => x.id !== PoDetailTypeCode.Order);
    }

    this.isPageReady = true;
  }

  getPngDialogService() {
    return this.domainService.pngDialogService;
  }

  hasJoDetail() {
    return this.poDetail.joDetailId != null;
  }

  searchAccounts(query: string){
    this.matchingAccounts = this.allAccounts.filter(a => a.accountName.toLowerCase().indexOf(query.toLowerCase()) > -1);
  }

  async lookupProduct() {
    const r = await EntityFinderDialog.open(ProductFinderDialogComponent, this, {
      manufacturer: this.poHeader.manufacturer
    });
    const product = _.first(r) as Product;
    if (product == null) {
      return false;
    }
    this.poDetail.productId = product.id;
    this.poDetail.origProductId = product.id;
  }

  async lookupAccount() {
    const r = await EntityFinderDialog.open(AccountFinderDialogComponent, this, {
      shouldShowReissueAccountsOnly: true
    });
    if (r.length == 0) return;
    const account = r[0];
    this.poDetail.accountId = account.id;
  }

  // called from html template
  getErrorMessage(propName: string) {
    const ea = this.poDetail && this.poDetail.entityAspect;
    if (ea == null) {
      return;
    }
    const errors = ea.getValidationErrors(propName);
    // if (errors.length === 0) {
    //   return;
    // }
    return errors.map(e => e.errorMessage).join('; ');

  }

  private getValidationErrorsAsHtml() {
    const ea = this.poDetail && this.poDetail.entityAspect;
    if (ea == null) {
      return '';
    }
    const errors = ea.getValidationErrors().map(e => e.errorMessage);
    if (this.poDetail.productId === 0) {
      errors.unshift('Product must be specified');
    }
    if (errors.length === 0) {
      return '';
    }
    const msg = errors.join('<br>');
    return msg;
  }

  async save() {
    const msg = this.getValidationErrorsAsHtml();
    if (msg.length > 0) {
      await this.dialogService.showOkMessage('Validation Errors', msg);
      return false;
    }

    await this.dbSaveService.saveChanges();
    this.dialogRef.close(true);
  }

  cancel() {
    this.uow.rollback();
    this.dialogRef.close(false);
  }

}
